<template>
  <VContainer>
    <VLayout
      row
      align-center
      justify-center
    >
      <VFlex
        xs12
        md10
      >
        <EditContactForm
          ref="editContactForm"
          :total-count="totalCount"
          @sliderChange="onSliderChanged"
          @sliderStart="onSliderStarted"
          @changed="onFormChanged"
        />
      </VFlex>
      <VBtn
        :disabled="!hasPreviousPage"
        fab
        small
        left
        color="grey lighten-3"
        dark
        absolute
        @click.stop="onFetchPrevContact"
        @keyup.native.left="onFetchPrevContact"
      >
        <VIcon color="black">
          keyboard_arrow_left
        </VIcon>
      </VBtn>
      <VBtn
        :disabled="!hasNextPage"
        fab
        small
        right
        color="grey lighten-3"
        dark
        absolute
        black
        @click.stop="onFetchNextContact"
        @keyup.native.right="onFetchNextContact"
      >
        <VIcon color="black">
          keyboard_arrow_right
        </VIcon>
      </VBtn>
    </VLayout>
    <NetworkSnackBar />
    <ProgressDialogCard
      :description="statusMessage"
      :dialog="loadingStatus"
      indeterminate
    />
  </VContainer>
</template>

<script>
import EditContactForm from '@/components/EditContactForm'
import QUERY_CONTACTS_TO_EDIT_LIST from '@/backend/graphql/contact/ContactsToEditList.gql'
import { mapGetters, mapActions } from 'vuex'
import { NetworkStatus } from 'apollo-client/core/networkStatus'
import NetworkSnackBar from '../../components/NetworkSnackBar'
import ProgressDialogCard from '../../components/ProgressDialogCard'
export default {
  name: 'EditContactsPage',
  components: {
    ProgressDialogCard,
    NetworkSnackBar,
    EditContactForm
  },
  props: {
    getGqlQuery: {
      type: Object,
      default: function () {
        return QUERY_CONTACTS_TO_EDIT_LIST
      }
    },
    getTotalCount: {
      type: Function,
      default: function (data) {
        return data.listContacts.totalCount
      }
    },
    getContacts: {
      type: Function,
      default: function (data) {
        return data.listContacts.results
      }
    },
    getQueryVariables: {
      type: Function,
      default: function () {
        return {
          limit: this.limit,
          offset: this.offset,
          orderBy: this.orderBy,
          isDelete: 0
        }
      }
    },
    updateQueryVariable: {
      type: Function,
      default: function (key, value) {
        if (this.hasOwnProperty(key)) {
          this[key] = value
        }
      }
    }
  },
  data: function () {
    return {
      limit: 30,
      offset: 0,
      orderBy: '-id,-last_recv_at',
      sliderLoading: false,
      contacts: [{
        id: '',
        number: '',
        name: '',
        lastName: '',
        avatarImg: null,
        gender: null
      }],
      totalCount: 0,
      snackBarTimeout: 6000,
      isContactDataChanged: false
    }
  },
  apollo: {
    contacts: {
      query () {
        return this.getGqlQuery
      },
      variables () {
        return this.getQueryVariables()
      },
      manual: true,
      result ({ data, loading, networkStatus }) {
        this.setQueryStatus(networkStatus)
        if (!loading && networkStatus !== NetworkStatus.error) {
          this.parseContactData(data)
        }
      },
      error (error) {
        this.loadingStatus = false
        this.$store.dispatch('alert/error', error.message)
      }
    }
  },
  computed: {
    hasPreviousPage () {
      if (this.allContacts.length === 0) return false
      return this.contactIndex > 0
    },
    hasNextPage () {
      if (this.allContacts.length === 0) return false
      return this.contactIndex < this.allContacts.length - 1
    },
    contactIndex: {
      get: function () {
        return this.$store.state.contact.contactIndex
      },
      set: function (val) {
        if (val || val === 0) {
          this.$store.dispatch('contact/setIndex', { val })
        }
      }
    },
    loadingStatus: {
      get: function () {
        return this.$store.getters['contact/getLoadingStatus']
      },
      set: function (val) {
        this.$store.dispatch('contact/setLoadingStatus', { val })
      }
    },
    statusMessage: {
      get: function () {
        return this.$store.getters['contact/getLoadingText']
      },
      set: function (message) {
        this.$store.dispatch('contact/setStatusMessage', { message })
      }
    },
    ...mapGetters({
      allContacts: 'contact/getAllContacts'
    })
  },
  created () {
    window.addEventListener('keyup', this.onBindingKeyboard)
    this.loadingStatus = true
    this.statusMessage = 'Loading Contacts. Please wait...'
    this.setEditMenuItems({ val: [] })
  },
  beforeDestroy () {
    window.removeEventListener('keyup', this.onBindingKeyboard)
    this.$store.dispatch('contact/clear')
  },
  methods: {
    ...mapActions({ setEditMenuItems: 'toolBar/setEditMenuItems' }),
    parseContactData (data) {
      this.totalCount = this.getTotalCount(data)
      this.contacts = this.getContacts(data)
      // console.log(this.contacts)
      if (this.allContacts.length === 0) {
        this.$store.dispatch('contact/init', {
          newContacts: this.contacts,
          totalCount: this.totalCount,
          isFullArraySize: true
        })
      } else if (this.getQueryVariables().offset >= this.contactIndex) {
        this.$store.dispatch('contact/next', {
          newContacts: this.contacts,
          startIndex: this.getQueryVariables().offset,
          setIndex: this.getQueryVariables().offset,
          updateMessage: true
        })
      } else if (this.getQueryVariables().offset < this.contactIndex) {
        this.$store.dispatch('contact/next', {
          newContacts: this.contacts,
          startIndex: this.getQueryVariables().offset,
          setIndex: this.sliderLoading ? this.contactIndex : this.contactIndex - 1,
          updateMessage: true
        })
      }
      this.sliderLoading = false
    },
    isContactEmpty (contact) {
      return !contact || Object.keys(contact).length === 0 || !contact.hasOwnProperty('id')
    },
    onBindingKeyboard (event) {
      switch (event.key) {
        case 'Left':
        case 'ArrowLeft': // left
          this.onFetchPrevContact()
          break
        case 'Right':
        case 'ArrowRight': // right
          this.onFetchNextContact()
          break
        default:
          break
      }
    },
    onFetchNextContact () {
      if (!this.hasNextPage || this.loadingStatus) return
      this.onPageChanged(this.contactIndex)
      const nextContact = this.allContacts[this.contactIndex + 1]
      if (this.isContactEmpty(nextContact)) {
        this.updateQueryVariable('offset', this.contactIndex + 1)
      } else {
        this.contactIndex = this.contactIndex + 1
      }
    },
    onFetchPrevContact () {
      if (!this.hasPreviousPage || this.loadingStatus) return
      this.onPageChanged(this.contactIndex)
      const prevContact = this.allContacts[this.contactIndex - 1]
      if (this.isContactEmpty(prevContact)) {
        this.updateQueryVariable('offset', this.contactIndex - this.getQueryVariables().limit < 0
          ? 0 : this.contactIndex - this.getQueryVariables().limit)
      } else {
        this.contactIndex = this.contactIndex - 1
      }
    },
    onSliderChanged (val) {
      const currentIndex = val - 1
      if (currentIndex > -1 && currentIndex < this.totalCount) {
        const contact = this.allContacts[val - 1]
        if (this.isContactEmpty(contact)) {
          // set slider loading to check the index while fetching contacts
          this.sliderLoading = true
          if (currentIndex >= this.getQueryVariables().offset) {
            this.updateQueryVariable('offset', currentIndex)
          } else {
            this.updateQueryVariable('offset',
              currentIndex - (this.getQueryVariables().limit - 1) < 0
                ? 0 : currentIndex - (this.getQueryVariables().limit - 1))
          }
        }
      }
      this.isContactDataChanged = false
    },
    onSliderStarted (val) {
      this.onPageChanged(val - 1)
    },
    updateData (index) {
      const contactData = Object.assign({}, this.allContacts[index])
      this.$store.dispatch('contact/updateContact', { contactData, index })
    },
    onPageChanged (index) {
      // index is the index of the changed contact
      if (this.isContactDataChanged) {
        this.updateData(index)
      }
      this.$refs.editContactForm.resetInitialValue(false)
      this.isContactDataChanged = false
      // console.log('@onPageChanged isContactDataChanged set', this.isContactDataChanged)
    },
    onFormChanged () {
      this.isContactDataChanged = true
      // console.log('@onFormChanged isContactDataChanged set', this.isContactDataChanged)
    },
    setQueryStatus (networkStatus) {
      switch (networkStatus) {
        case NetworkStatus.loading:
          this.loadingStatus = true
          this.statusMessage = 'Loading contacts. Please wait...'
          break
        case NetworkStatus.setVariables:
          this.loadingStatus = true
          this.statusMessage = 'Loading contacts. Please wait...'
          break
        case NetworkStatus.ready:
          break
        case NetworkStatus.poll:
          break
        case NetworkStatus.error:
          this.loadingStatus = false
          break
        default:
          break
      }
    }
  }
}
</script>

<style scoped>

</style>
